import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { CarCreate, Car } from "@shared/schemas/cars";
import { regions } from "@shared/schema";
import { ArrowLeft, Car as CarIcon, ShieldCheck } from "lucide-react";
import { z } from "zod";
import { PageContainer } from "@/components/layout/PageContainer";
import { MarketImageUploader } from "@/components/MarketImageUploader";

// 호주 자동차 브랜드 목록
const carMakes = [
  "Toyota", "Hyundai", "Mazda", "Ford", "Mitsubishi", "Kia", "Nissan",
  "Honda", "Volkswagen", "Subaru", "BMW", "Mercedes-Benz", "Audi",
  "Lexus", "Volvo", "Jeep", "Land Rover", "Porsche", "Tesla", "Other"
];

// Body Type 목록
const bodyTypes = ["SUV", "Hatch", "Sedan", "Ute", "Van", "Wagon", "Coupe", "Convertible"];

// Transmission 목록
const transmissions = ["Auto", "Manual"];

// Fuel Type 목록
const fuelTypes = ["Petrol", "Diesel", "Electric", "Hybrid", "LPG"];

// Rego Status 목록
const regoStatuses = ["Yes", "No", "Expired"];

const formSchema = z.object({
  seller_type: z.enum(["private", "dealer"]).default("private"),
  business_id: z.string().optional(),
  make: z.string().min(1, "브랜드를 선택해주세요"),
  model: z.string().min(1, "모델명을 입력해주세요"),
  year: z.number().int().min(1900).max(2100),
  mileage: z.number().int().min(0).optional(),
  body_type: z.enum(["SUV", "Hatch", "Sedan", "Ute", "Van", "Wagon", "Coupe", "Convertible"]).optional(),
  transmission: z.enum(["Auto", "Manual"]).optional(),
  fuel_type: z.enum(["Petrol", "Diesel", "Electric", "Hybrid", "LPG"]).optional(),
  color: z.string().optional(),
  vin: z.string().max(17).optional(),
  rego_status: z.enum(["Yes", "No", "Expired"]).optional(),
  rego_expiry_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  rwc_available: z.boolean().default(false),
  roadworthy_certificate: z.boolean().default(false),
  price: z.number().int().min(0),
  price_type: z.enum(["fixed", "negotiable"]).default("fixed"),
  description: z.string().max(5000).optional(),
  state: z.string().min(1, "주/지역을 선택해주세요"),
  city: z.string().min(1, "도시를 선택해주세요"),
  suburb: z.string().optional(),
  postcode: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  images: z.array(z.string()).max(20).default([]),
  primary_image: z.string().optional(),
  status: z.enum(["available", "reserved", "sold"]).default("available"),
  is_premium: z.boolean().default(false),
  premium_until: z.string().datetime().optional(),
  expires_at: z.string().datetime().optional(),
});

export default function CarForm() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState<string>("");
  const [priceType, setPriceType] = useState<"fixed" | "negotiable">("fixed");
  const [priceDisplay, setPriceDisplay] = useState<string>("");
  const [images, setImages] = useState<string[]>([]);

  // Check if we're in edit mode
  const [match, params] = useRoute<{ id: string }>("/cars/edit/:id");
  const carId = match && params ? params.id : null;

  // Fetch existing car if in edit mode
  const { data: carData, isLoading: loadingCar } = useQuery<{ ok: boolean; data: Car }>({
    queryKey: ["/api/cars", carId],
    queryFn: async () => {
      const res = await fetch(`/api/cars/${carId}`);
      if (!res.ok) throw new Error("Failed to fetch car");
      return res.json();
    },
    enabled: !!carId,
  });

  const existingCar = carData?.data;

  // Calculate expiry date (30 days from now)
  const getExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString();
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      seller_type: "private",
      business_id: undefined,
      make: "",
      model: "",
      year: new Date().getFullYear(),
      mileage: undefined,
      body_type: undefined,
      transmission: undefined,
      fuel_type: undefined,
      color: "",
      vin: "",
      rego_status: undefined,
      rego_expiry_date: "",
      rwc_available: false,
      roadworthy_certificate: false,
      price: 0,
      price_type: "fixed",
      description: "",
      state: "",
      city: "",
      suburb: "",
      postcode: "",
      latitude: undefined,
      longitude: undefined,
      images: [],
      primary_image: undefined,
      status: "available",
      is_premium: false,
      premium_until: undefined,
      expires_at: getExpiryDate(),
    },
  });

  // Populate form with existing car data
  useEffect(() => {
    if (existingCar && carId) {
      form.reset({
        seller_type: existingCar.seller_type as "private" | "dealer",
        business_id: existingCar.business_id || undefined,
        make: existingCar.make,
        model: existingCar.model,
        year: existingCar.year,
        mileage: existingCar.mileage || undefined,
        body_type: existingCar.body_type as any,
        transmission: existingCar.transmission as any,
        fuel_type: existingCar.fuel_type as any,
        color: existingCar.color || "",
        vin: existingCar.vin || "",
        rego_status: existingCar.rego_status as any,
        rego_expiry_date: existingCar.rego_expiry_date || "",
        rwc_available: existingCar.rwc_available,
        roadworthy_certificate: existingCar.roadworthy_certificate,
        price: existingCar.price,
        price_type: existingCar.price_type as "fixed" | "negotiable",
        description: existingCar.description || "",
        state: existingCar.state,
        city: existingCar.city,
        suburb: existingCar.suburb || "",
        postcode: existingCar.postcode || "",
        latitude: existingCar.latitude || undefined,
        longitude: existingCar.longitude || undefined,
        images: existingCar.images || [],
        primary_image: existingCar.primary_image || undefined,
        status: existingCar.status as "available" | "reserved" | "sold",
        is_premium: existingCar.is_premium,
        premium_until: existingCar.premium_until || undefined,
        expires_at: existingCar.expires_at || getExpiryDate(),
      });
      setPriceDisplay(String(existingCar.price / 100));
      setPriceType(existingCar.price_type as "fixed" | "negotiable");
      setImages(existingCar.images || []);
    }
  }, [existingCar, carId, form]);

  const createMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const token = await user?.getIdToken();
      const priceInCents = priceType === "negotiable" ? -1 : parseInt(priceDisplay) * 100;

      const carData: CarCreate = {
        seller_type: data.seller_type,
        business_id: data.business_id,
        make: data.make,
        model: data.model,
        year: data.year,
        mileage: data.mileage,
        body_type: data.body_type,
        transmission: data.transmission,
        fuel_type: data.fuel_type,
        color: data.color,
        vin: data.vin,
        rego_status: data.rego_status,
        rego_expiry_date: data.rego_expiry_date,
        rwc_available: data.rwc_available,
        roadworthy_certificate: data.roadworthy_certificate,
        price: priceInCents,
        price_type: priceType,
        description: data.description,
        state: data.state,
        city: data.city,
        suburb: data.suburb,
        postcode: data.postcode,
        latitude: data.latitude,
        longitude: data.longitude,
        images: images,
        primary_image: images[0] || undefined,
        status: data.status,
        is_premium: data.is_premium,
        premium_until: data.premium_until,
        expires_at: data.expires_at,
      };

      return apiRequest("POST", "/api/cars", carData, {
        Authorization: `Bearer ${token}`,
      });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/cars"] });
      toast({
        title: "중고차가 등록되었습니다",
        description: "등록이 완료되었습니다.",
      });
      setLocation(`/cars/${data.data?.id || data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const token = await user?.getIdToken();
      const priceInCents = priceType === "negotiable" ? -1 : parseInt(priceDisplay) * 100;

      const carData: Partial<CarCreate> = {
        seller_type: data.seller_type,
        business_id: data.business_id,
        make: data.make,
        model: data.model,
        year: data.year,
        mileage: data.mileage,
        body_type: data.body_type,
        transmission: data.transmission,
        fuel_type: data.fuel_type,
        color: data.color,
        vin: data.vin,
        rego_status: data.rego_status,
        rego_expiry_date: data.rego_expiry_date,
        rwc_available: data.rwc_available,
        roadworthy_certificate: data.roadworthy_certificate,
        price: priceInCents,
        price_type: priceType,
        description: data.description,
        state: data.state,
        city: data.city,
        suburb: data.suburb,
        postcode: data.postcode,
        latitude: data.latitude,
        longitude: data.longitude,
        images: images,
        primary_image: images[0] || undefined,
        status: data.status,
        is_premium: data.is_premium,
        premium_until: data.premium_until,
        expires_at: data.expires_at,
      };

      return apiRequest("PATCH", `/api/cars/${carId}`, carData, {
        Authorization: `Bearer ${token}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cars", carId] });
      queryClient.invalidateQueries({ queryKey: ["/api/cars"] });
      toast({
        title: "중고차 정보가 수정되었습니다",
        description: "수정이 완료되었습니다.",
      });
      setLocation(`/cars/${carId}`);
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (!priceDisplay && priceType === "fixed") {
      toast({
        title: "가격을 입력해주세요",
        variant: "destructive",
      });
      return;
    }

    if (carId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  if (loadingCar) {
    return (
      <PageContainer size="default">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer size="default">
      <Button
        variant="ghost"
        onClick={() => setLocation("/cars")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        목록으로
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            {carId ? "중고차 정보 수정" : "중고차 등록"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* 판매자 유형 */}
              <FormField
                control={form.control}
                name="seller_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>판매자 유형</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) => {
                          field.onChange(value);
                          if (value === "dealer" && !(user as { businessId?: string } | null)?.businessId) {
                            toast({
                              title: "딜러 등록 필요",
                              description: "딜러로 등록하려면 먼저 딜러 인증을 완료해주세요.",
                              action: (
                                <Button
                                  size="sm"
                                  onClick={() => setLocation("/cars/dealer/register")}
                                >
                                  딜러 등록하기
                                </Button>
                              ),
                            });
                          }
                        }}
                        value={field.value}
                        className="flex flex-row space-x-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="private" id="private" />
                          <label htmlFor="private">개인</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="dealer" id="dealer" />
                          <label htmlFor="dealer">딜러</label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 차량 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="make"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>브랜드 *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="브랜드 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {carMakes.map((make) => (
                            <SelectItem key={make} value={make}>
                              {make}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="model"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>모델 *</FormLabel>
                      <FormControl>
                        <Input placeholder="예: Camry" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>연식 *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1900"
                          max="2100"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mileage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>주행거리 (km)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="예: 50000"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="body_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>차종</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="차종 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {bodyTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="transmission"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>변속기</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="변속기 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {transmissions.map((trans) => (
                            <SelectItem key={trans} value={trans}>
                              {trans}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fuel_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>연료</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="연료 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {fuelTypes.map((fuel) => (
                            <SelectItem key={fuel} value={fuel}>
                              {fuel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>색상</FormLabel>
                      <FormControl>
                        <Input placeholder="예: White" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* 호주 특화 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" />
                  호주 특화 정보
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="rego_status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rego 상태</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Rego 상태" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {regoStatuses.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="rego_expiry_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rego 만료일</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            placeholder="YYYY-MM-DD"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Rego 만료일을 입력해주세요 (선택)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="rwc_available"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>RWC 보유</FormLabel>
                          <FormDescription>
                            Roadworthy Certificate 보유 여부
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VIN 번호 (선택)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Vehicle Identification Number"
                            maxLength={17}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          VIN 번호는 PPSR Check에 사용됩니다
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* 가격 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">가격 정보</h3>

                <div className="flex gap-4">
                  <RadioGroup
                    value={priceType}
                    onValueChange={(value) => {
                      setPriceType(value as "fixed" | "negotiable");
                      form.setValue("price_type", value as "fixed" | "negotiable");
                    }}
                    className="flex flex-row space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fixed" id="fixed" />
                      <label htmlFor="fixed">고정가격</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="negotiable" id="negotiable" />
                      <label htmlFor="negotiable">가격협의</label>
                    </div>
                  </RadioGroup>
                </div>

                {priceType === "fixed" && (
                  <FormField
                    control={form.control}
                    name="price"
                    render={() => (
                      <FormItem>
                        <FormLabel>가격 (AUD) *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            placeholder="예: 25000"
                            value={priceDisplay}
                            onChange={(e) => {
                              setPriceDisplay(e.target.value);
                              form.setValue(
                                "price",
                                e.target.value ? parseInt(e.target.value) * 100 : 0
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* 위치 정보 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">위치 정보</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>주/지역 *</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            setSelectedState(value);
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="주 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {regions.map((region) => (
                              <SelectItem key={region.id} value={region.id}>
                                {region.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>도시 *</FormLabel>
                        <FormControl>
                          <Input placeholder="예: Sydney" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="suburb"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Suburb</FormLabel>
                        <FormControl>
                          <Input placeholder="예: Epping" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="postcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>우편번호</FormLabel>
                        <FormControl>
                          <Input placeholder="예: 2121" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* 상세 설명 */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>상세 설명</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="차량 상태, 정비 내역, 옵션 등을 자세히 입력해주세요"
                        rows={6}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 이미지 업로드 */}
              <FormField
                control={form.control}
                name="images"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>이미지 (최대 20장)</FormLabel>
                    <FormControl>
                      <MarketImageUploader
                        maxImages={20}
                        initialImages={images}
                        onImagesChange={(newImages) => {
                          setImages(newImages);
                          field.onChange(newImages);
                          if (newImages.length > 0) {
                            form.setValue("primary_image", newImages[0]);
                          }
                        }}
                        userId={user?.uid}
                      />
                    </FormControl>
                    <FormDescription>
                      차량 사진을 업로드해주세요. 첫 번째 이미지가 대표 이미지로 사용됩니다.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 제출 버튼 */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {carId ? "수정하기" : "등록하기"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/cars")}
                >
                  취소
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
